// @ts-strict-ignore
import Layout from 'antd/lib/layout';
import * as React from 'react';
import { NavigationBar } from '../sharedComponents/NavigationBar';
import { ThunderboltFilled } from '@ant-design/icons';
import { Route, RouteComponentProps, Switch, useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { IntegrationCapability } from '../../types';
import {
    createAppRoute,
    createAutomationsToolboxIntegrationCapabilityRoute,
    createAutomationsToolboxRoute,
} from '../../helpers/routeHelpers';
import { selectAppSettings } from '../../selectors/selectors';
import { AutomationsListContainer } from './AutomationsListContainer';
import { displayTextForAppPlatform } from '../../helpers/displayHelpers';
import { useMdmPlatformOverride } from '../../helpers/mdmPlatformOverride';
import { AssociatedIntegrationsBar } from '../setup/SetupStepsContainer';
import { ConnectIntegrationsContainer } from '../integrations/ConnectIntegrationsContainer';
import { AppComponentProps, AppRouteComponentMatchParams } from '../../typescriptHelpers';
import routes from '../../routes';

export const AutomationsToolboxContainer: React.FC<AppComponentProps> = ({ appId, orgId }) => {
    const { orgIdParam, appIdParam } = useParams<AppRouteComponentMatchParams>();
    const history = useHistory();
    const matchesToolboxIntegrations = useRouteMatch(routes.automationsToolboxIntegrations);

    const headerTitle = 'Runway automations';
    const headerDescription = 'Discover and set up automations that allow Runway to perform actions for you.';

    const { appName, platform, appStoreType, appImageUrl } = useSelector((state: RootState) => {
        const appSettings = selectAppSettings(state, appId);

        return {
            appName: appSettings.appName,
            platform: appSettings.platform,
            appStoreType: appSettings.appStoreType,
            appImageUrl: appSettings.appImageUrl,
        };
    });

    const mdmPlatformLabel = useMdmPlatformOverride(appId)?.label;

    const onCloseConnectIntegrations = (automationId: string) => {
        const route = createAutomationsToolboxRoute(orgIdParam, appIdParam);
        const state = {
            automationId,
        };

        history.push(route, state);
    };

    const onShowConnectIntegrations = (capability: IntegrationCapability, automationId: string) => {
        const route = createAutomationsToolboxIntegrationCapabilityRoute(orgIdParam, appIdParam, capability);
        const state = {
            automationId,
        };

        history.push(route, state);
    };

    return (
        <Layout>
            <NavigationBar
                style={{ minHeight: 120, height: 120 }}
                hideCloseX
                hideDoneButton={!!matchesToolboxIntegrations}
                isSingleStep
                title={headerTitle}
                description={headerDescription}
                icon={ThunderboltFilled}
                onClose={() => {
                    history.push(createAppRoute(orgId, appId), { shouldShowSuccessBanner: false });
                }}
            />
            <Switch>
                <Route
                    path={routes.automationsToolboxIntegrations}
                    exact
                    render={(routeComponentProps: RouteComponentProps) => {
                        const params = routeComponentProps.match.params;
                        const state = routeComponentProps.history.location.state;
                        const goBackToAutomationId = state ? state['automationId'] : null;

                        return (
                            <ConnectIntegrationsContainer
                                appId={appId}
                                orgId={orgId}
                                capability={params['integrationCapability']}
                                onCloseConnectIntegrations={() => {
                                    return onCloseConnectIntegrations(goBackToAutomationId);
                                }}
                            />
                        );
                    }}
                ></Route>
                <Route
                    path={routes.automationsToolbox}
                    render={(routeComponentProps: RouteComponentProps) => {
                        const state = routeComponentProps.history.location.state;

                        return (
                            <AutomationsListContainer
                                appId={appId}
                                orgId={orgId}
                                onShowConnectIntegrations={onShowConnectIntegrations}
                                scrollToAutomation={state ? state['automationId'] : null}
                            />
                        );
                    }}
                ></Route>
            </Switch>
            <AssociatedIntegrationsBar
                orgId={orgId}
                appId={appId}
                appText={`${appName} (${mdmPlatformLabel ?? displayTextForAppPlatform(platform, appStoreType)})`}
                appImageUrl={appImageUrl}
            />
        </Layout>
    );
};
